import { useState } from 'react';
import { Lightbox } from './Lightbox';
import { Screen } from './Screen';

export interface PersonPageProps {
  role: string;
  name: string;
  tagline: string;
  bio: string;
  interests: string[];
  hero: string;
  gallery: string[];
}

/** Bride and Groom share this exact layout so they read as a matched pair. */
export function PersonPage({
  role,
  name,
  tagline,
  bio,
  interests,
  hero,
  gallery,
}: PersonPageProps) {
  // Hero first, then the gallery — one ordered list the lightbox pages through.
  const photos = [hero, ...gallery];
  const [active, setActive] = useState<number | null>(null);

  return (
    <Screen>
      <button
        type="button"
        onClick={() => setActive(0)}
        aria-label={`Enlarge photo of ${name}`}
        style={{ display: 'block', width: '100%', padding: 0, border: 'none', background: 'none' }}
      >
        <img
          src={hero}
          alt={name}
          style={{
            width: '100%',
            aspectRatio: '4 / 5',
            objectFit: 'cover',
            borderRadius: 'var(--radius)',
            display: 'block',
            cursor: 'zoom-in',
          }}
        />
      </button>
      <p className="eyebrow" style={{ marginTop: 22 }}>
        {role}
      </p>
      <h1 className="serif" style={{ fontSize: '2.6rem', fontWeight: 500, margin: '4px 0 0' }}>
        {name}
      </h1>
      <p
        className="serif"
        style={{
          fontStyle: 'italic',
          fontSize: '1.25rem',
          color: 'var(--text-dim)',
          margin: '14px 0 0',
        }}
      >
        “{tagline}”
      </p>
      <p style={{ color: 'var(--text-dim)', lineHeight: 1.6, marginTop: 14 }}>{bio}</p>
      {interests.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 18 }}>
          {interests.map((interest) => (
            <span
              key={interest}
              style={{
                border: '1px solid var(--gold)',
                color: 'var(--gold)',
                borderRadius: 999,
                padding: '6px 14px',
                fontSize: 13,
              }}
            >
              {interest}
            </span>
          ))}
        </div>
      )}
      {gallery.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 20 }}>
          {gallery.map((src, i) => (
            <button
              key={src}
              type="button"
              onClick={() => setActive(i + 1)}
              aria-label="Enlarge photo"
              style={{ padding: 0, border: 'none', background: 'none' }}
            >
              <img
                src={src}
                alt=""
                style={{
                  width: '100%',
                  aspectRatio: '1',
                  objectFit: 'cover',
                  borderRadius: 10,
                  display: 'block',
                  cursor: 'zoom-in',
                }}
              />
            </button>
          ))}
        </div>
      )}

      <Lightbox
        images={photos}
        index={active}
        onClose={() => setActive(null)}
        onIndex={setActive}
      />
    </Screen>
  );
}
